import { Link } from 'react-router-dom';
import { useSEO } from '@/lib/seo';
import { IDS } from '@/constants/testIds';
import { APP } from '@/constants/brand';
import { SPONSOR } from '@/constants/sponsor';
import CoreyAvatar from '@/components/CoreyAvatar';
import { trackSponsorClick } from '@/lib/tracking';
import {
  Wrench, Mic, Camera, Zap, Droplets, Hammer, Paintbrush, Home, Fan, Phone,
  Sparkles, ArrowRight, Star,
} from 'lucide-react';

const TRADES = [
  { name: 'Electrical', icon: Zap, color: 'text-amber-400', bg: 'hover:bg-amber-500/10' },
  { name: 'Plumbing', icon: Droplets, color: 'text-sky-400', bg: 'hover:bg-sky-500/10' },
  { name: 'Carpentry', icon: Hammer, color: 'text-orange-400', bg: 'hover:bg-orange-500/10' },
  { name: 'HVAC', icon: Fan, color: 'text-lime-400', bg: 'hover:bg-lime-500/10' },
  { name: 'Painting', icon: Paintbrush, color: 'text-pink-400', bg: 'hover:bg-pink-500/10' },
  { name: 'Roofing', icon: Home, color: 'text-red-400', bg: 'hover:bg-red-500/10' },
  { name: 'Appliances', icon: Wrench, color: 'text-purple-400', bg: 'hover:bg-purple-500/10' },
  { name: 'Anything', icon: Sparkles, color: 'text-yellow-300', bg: 'hover:bg-yellow-500/10' },
];

export default function LandingPage() {
  useSEO({
    title: 'DIY X-treme — AI Repair Guide for Every Trade | diybot.org',
    description: 'Broken outlet? Leaky faucet? Wobbly deck? Describe your problem and get a step-by-step AI repair guide with tool lists, material costs, safety warnings, and expert help from Corey or Christine — your DIY buddy in your pocket.',
    canonical: 'https://diybot.org/',
  });
  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Floating blobs */}
      <div className="blob bg-amber-500" style={{ width: 500, height: 500, top: -100, left: -100 }} />
      <div className="blob bg-lime-500" style={{ width: 400, height: 400, top: 100, right: -50, animationDelay: '2s' }} />
      <div className="blob bg-sky-500" style={{ width: 350, height: 350, bottom: 200, left: 200, animationDelay: '5s' }} />

      {/* Hero */}
      <section
        data-testid={IDS.landingHero}
        className="relative border-b border-zinc-800 overflow-hidden"
      >
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-950/60 to-zinc-950" />

        <div className="relative max-w-6xl mx-auto px-6 py-24 sm:py-32">
          <div className="fade-up inline-flex items-center gap-2 border border-amber-500/40 bg-amber-500/10 backdrop-blur px-4 py-2 mb-8">
            <Sparkles size={14} className="text-amber-400 pulse-gentle" />
            <span className="font-mono text-xs text-amber-300 uppercase tracking-[0.25em]">
              AI Handyman · In Your Pocket
            </span>
          </div>

          <h1 className="fade-up delay-100 font-display text-6xl sm:text-8xl lg:text-9xl leading-[0.9] tracking-tight max-w-5xl">
            <span className="block text-zinc-100">DIY</span>
            <span className="block" style={{ letterSpacing: '-0.04em' }}>
              <span className="text-gradient-hot">X</span><span className="text-zinc-100 text-4xl sm:text-6xl lg:text-7xl align-middle ml-1">-TREME.</span>
            </span>
          </h1>

          <p className="fade-up delay-200 text-zinc-300 text-xl sm:text-2xl mt-8 max-w-2xl leading-relaxed">
            <span className="text-zinc-100 font-semibold">Describe your problem. We'll diagnose, teach, and guide you.</span>
            <span className="block mt-3 text-zinc-400 text-base">Learn skills. Solve problems. Work smarter — whether you're a homeowner, a tradesman, or a student.</span>
          </p>

          {/* Guide intro card (shows default Corey — user picks their own after signup) */}
          <div className="fade-up delay-300 mt-8 inline-flex items-center gap-4 border border-zinc-800 bg-zinc-900/70 backdrop-blur pl-3 pr-6 py-3 max-w-lg">
            <CoreyAvatar size="md" />
            <div className="text-left">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">
                Meet Your DIY Buddy
              </div>
              <div className="font-display text-xl text-zinc-100 leading-tight">
                COREY OR CHRISTINE — YOU PICK.
              </div>
              <div className="text-zinc-400 text-sm">
                Two friendly experts. Same great fixes. Choose your buddy after sign-up.
              </div>
            </div>
          </div>

          <div className="fade-up delay-300 mt-8 flex flex-wrap gap-4">
            <Link
              to="/auth?mode=signup"
              data-testid={IDS.landingCtaStart}
              className="btn-primary text-base hover-wiggle"
            >
              <Sparkles size={18} /> Let's Fix Something
              <ArrowRight size={18} />
            </Link>
            <Link to="/auth?mode=login" className="btn-secondary text-base">
              Log In
            </Link>
          </div>

          {/* Feature bullets */}
          <div className="fade-up delay-400 mt-16 grid sm:grid-cols-3 gap-5 max-w-4xl">
            {[
              { icon: Mic, title: 'Just Talk', text: 'Voice-describe the problem. We transcribe it in seconds.', border: 'border-t-amber-400', color: 'text-amber-400', glow: 'glow-amber' },
              { icon: Camera, title: 'Snap a Pic', text: 'Photo of the mess? AI reads it and diagnoses instantly.', border: 'border-t-lime-400', color: 'text-lime-400', glow: 'glow-lime' },
              { icon: Wrench, title: 'Fix It', text: 'Clear steps, exact tools, real prices. No mystery.', border: 'border-t-sky-400', color: 'text-sky-400', glow: 'glow-sky' },
            ].map(({ icon: Icon, title, text, border, color, glow }) => (
              <div key={title} className={`industrial-card border-t-4 ${border} ${glow} hover:-translate-y-1 transition-all group`}>
                <Icon size={26} className={`${color} mb-3 group-hover:scale-110 transition-transform`} strokeWidth={2} />
                <div className="font-display text-2xl text-zinc-100 tracking-wide mb-1">{title}</div>
                <div className="text-zinc-400 text-sm">{text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trades grid */}
      <section className="relative max-w-6xl mx-auto px-6 py-20">
        <div className="mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400 mb-2">
              /every_trade
            </div>
            <h2 className="font-display text-4xl sm:text-6xl text-zinc-100 tracking-tight leading-none">
              IF IT'S BROKEN, <span className="text-gradient-cool">WE'VE GOT YOU.</span>
            </h2>
          </div>
          <div className="font-handwritten text-2xl text-amber-400 rotate-[-2deg]">
            ← 100+ types of fixes
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {TRADES.map(({ name, icon: Icon, color, bg }, i) => (
            <div
              key={name}
              className={`industrial-card flex flex-col items-start gap-3 hover:border-amber-500 hover:-translate-y-1 transition-all cursor-pointer group ${bg}`}
              style={{ animation: `fadeUp 0.6s ease-out ${i * 60}ms both` }}
            >
              <Icon size={30} className={`${color} group-hover:scale-125 group-hover:rotate-6 transition-transform`} strokeWidth={1.8} />
              <div className="font-display text-xl uppercase tracking-wide text-zinc-100">{name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial-style pull quote */}
      <section className="relative max-w-6xl mx-auto px-6 pb-16">
        <div className="industrial-card !p-10 border-t-4 border-t-lime-400 bg-gradient-to-br from-lime-500/10 via-sky-500/5 to-transparent glow-lime">
          <Star size={28} className="text-lime-400 mb-4 float-slow" fill="currentColor" />
          <p className="font-display text-3xl sm:text-4xl text-zinc-100 leading-tight tracking-tight max-w-3xl">
            "Fixed my garbage disposal at 11pm. My wife thinks I'm a <span className="text-gradient-hot">genius</span> now."
          </p>
          <div className="font-handwritten text-2xl text-amber-400 mt-4">— literally every user</div>
        </div>
      </section>

      {/* Sponsor spotlight */}
      <section className="relative max-w-6xl mx-auto px-6 pb-24">
        <div className="industrial-card border-t-4 border-t-amber-400 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent glow-amber">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-amber-400 flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30 float-slow-rev">
              <Zap size={26} className="text-zinc-950" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-1 flex items-center gap-2">
                Featured Local Pro
                <Star size={12} className="fill-amber-400 twinkle" />
                <Star size={12} className="fill-amber-400 twinkle twinkle-delay-1" />
                <Star size={12} className="fill-amber-400 twinkle twinkle-delay-2" />
                <Star size={12} className="fill-amber-400 twinkle twinkle-delay-3" />
                <Star size={12} className="fill-amber-400 twinkle twinkle-delay-4" />
              </div>
              <div className="font-display text-3xl text-zinc-100 tracking-wide">{SPONSOR.name}</div>
              <div className="text-zinc-400 mt-1">{SPONSOR.tagline} · {SPONSOR.city}</div>
            </div>
          </div>
          <a href={SPONSOR.phoneHref} onClick={() => trackSponsorClick('landing_spotlight')} className="btn-primary text-base">
            <Phone size={18} /> {SPONSOR.phone}
          </a>
        </div>
      </section>

      {/* Meet Your Buddies */}
      <section className="relative max-w-6xl mx-auto px-6 pb-24" id="buddies">
        <div className="mb-10 sm:mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-3">Your DIY buddies</div>
          <h2 className="font-display text-4xl sm:text-5xl text-zinc-100 tracking-tight leading-tight max-w-3xl">
            Meet <span className="text-gradient-hot">Corey & Christine.</span>
          </h2>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Two experts. Two voices. Same encyclopedic knowledge of every trade. Pick your vibe — you can switch any time.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
          <Link
            to="/auth?mode=signup&guide=corey"
            data-testid="landing-buddy-corey"
            className="industrial-card !p-0 overflow-hidden text-left transition-all group hover:border-amber-500 hover:-translate-y-1"
          >
            <div className="relative aspect-[4/5] bg-zinc-950 overflow-hidden">
              <img
                src="/corey.png"
                alt="Corey — DIY buddy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                style={{ objectPosition: 'center 20%' }}
              />
              <div className="absolute top-3 right-3 bg-zinc-950/80 backdrop-blur border border-amber-500/40 text-amber-400 font-mono text-[10px] uppercase tracking-widest px-2 py-1">
                Male voice · Deep
              </div>
            </div>
            <div className="p-4 sm:p-5 bg-zinc-900">
              <div className="font-display text-2xl sm:text-3xl text-zinc-100 tracking-wide mb-1">COREY</div>
              <div className="text-zinc-400 text-sm mb-3">
                Old-school journeyman electrician. Twenty years on the truck. Talks like he's been fixing this exact problem since Tuesday.
              </div>
              <div className="inline-flex items-center gap-2 text-amber-400 font-mono text-xs uppercase tracking-widest group-hover:text-amber-300">
                Sign up with Corey <ArrowRight size={14} />
              </div>
            </div>
          </Link>

          <Link
            to="/auth?mode=signup&guide=christine"
            data-testid="landing-buddy-christine"
            className="industrial-card !p-0 overflow-hidden text-left transition-all group hover:border-amber-500 hover:-translate-y-1"
          >
            <div className="relative aspect-[4/5] bg-zinc-950 overflow-hidden">
              <img
                src="/christine.png"
                alt="Christine — DIY buddy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                style={{ objectPosition: 'center 20%' }}
              />
              <div className="absolute top-3 right-3 bg-zinc-950/80 backdrop-blur border border-amber-500/40 text-amber-400 font-mono text-[10px] uppercase tracking-widest px-2 py-1">
                Female voice · Warm
              </div>
            </div>
            <div className="p-4 sm:p-5 bg-zinc-900">
              <div className="font-display text-2xl sm:text-3xl text-zinc-100 tracking-wide mb-1">CHRISTINE</div>
              <div className="text-zinc-400 text-sm mb-3">
                Master plumber turned instructor. Explains the "why" behind every step. Patient with beginners, sharp with pros.
              </div>
              <div className="inline-flex items-center gap-2 text-amber-400 font-mono text-xs uppercase tracking-widest group-hover:text-amber-300">
                Sign up with Christine <ArrowRight size={14} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative max-w-6xl mx-auto px-6 pb-24" id="how-it-works">
        <div className="mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400 mb-3">How it works</div>
          <h2 className="font-display text-4xl sm:text-5xl text-zinc-100 tracking-tight leading-tight max-w-3xl">
            Three steps between you and a <span className="text-gradient-hot">fixed problem.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              n: '01',
              t: 'Describe it',
              d: 'Type a quick description, upload a photo of the mess, or hit the mic and talk. Our AI reads photos, transcribes voice, and understands plumbing, electrical, HVAC, carpentry, appliances, roofing, painting — every trade.',
              icon: Mic,
              color: 'text-amber-400',
            },
            {
              n: '02',
              t: 'Get a step-by-step fix',
              d: 'Your buddy Corey or Christine walks you through the repair — exact steps, tools you need, materials with real prices, safety warnings when things are dangerous, and a heads-up when it is smarter to call a pro.',
              icon: Wrench,
              color: 'text-lime-400',
            },
            {
              n: '03',
              t: 'Actually fix it',
              d: 'Follow along at your own pace. Ask follow-up questions by voice or text. Watch an AI-generated demo video of your exact repair. If you get stuck, tap "Call a Pro" and we connect you to a vetted local tradesman.',
              icon: Zap,
              color: 'text-sky-400',
            },
          ].map((step) => (
            <div key={step.n} className="industrial-card border-t-4 border-t-zinc-800 hover:border-t-amber-500 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="font-display text-5xl text-zinc-800">{step.n}</div>
                <step.icon size={28} className={step.color} />
              </div>
              <h3 className="font-display text-2xl text-zinc-100 mb-2">{step.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative max-w-6xl mx-auto px-6 pb-24" id="features">
        <div className="mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-lime-400 mb-3">What's inside</div>
          <h2 className="font-display text-4xl sm:text-5xl text-zinc-100 tracking-tight leading-tight max-w-3xl">
            Everything a homeowner or tradesman needs, in one app.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { t: 'AI Repair Buddies', d: 'Pick Corey or Christine — each has a voice, personality, and expertise across every trade. Talk to them like a real coworker.' },
            { t: 'Voice + Photo Input', d: 'Speak your problem or snap a picture. Our AI reads nameplates, spots corrosion, identifies parts, and understands your accent.' },
            { t: 'Step-by-Step Videos', d: 'Watch an AI-generated demo of Corey or Christine performing your exact repair — great for visual learners.' },
            { t: 'Nameplate Scanner', d: 'Point your camera at any motor, breaker, HVAC unit, or appliance nameplate. Instantly parses voltage, amperage, horsepower, model number, and safety specs.' },
            { t: 'Real Material Costs', d: 'Every guide includes current material prices — no more "call the parts desk" surprises.' },
            { t: 'Call-a-Pro Backup', d: 'If a fix crosses into dangerous territory, tap once and connect to a vetted local tradesman. Contact Electric is our first local sponsor — more coming.' },
            { t: 'DIY Academy (Pro)', d: 'Go deeper: NEC electrical codes, plumbing theory, refrigeration cycles, structural load basics. $9.99/mo with a 7-day free trial.' },
            { t: 'Save to Truck', d: 'Tradesmen can tag scanned nameplates by truck, jobsite, or customer — pull the specs up anywhere, any time.' },
            { t: 'Refer a Friend', d: 'Share your referral code — you and your buddy both get bonus scans and Academy perks.' },
          ].map((f) => (
            <div key={f.t} className="industrial-card !p-5">
              <h3 className="font-display text-xl text-amber-400 mb-2">{f.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="relative max-w-4xl mx-auto px-6 pb-24" id="faq">
        <div className="mb-10">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-sky-400 mb-3">FAQ</div>
          <h2 className="font-display text-4xl sm:text-5xl text-zinc-100 tracking-tight leading-tight">
            Common questions.
          </h2>
        </div>
        <div className="space-y-4">
          {[
            {
              q: 'Is DIY X-treme free?',
              a: 'Yes. Every user gets unlimited AI repair guides, voice buddy, photo diagnosis, and a few free nameplate scans per week. The optional Academy tier ($9.99/mo) unlocks deep theory content and unlimited scans for tradesmen.',
            },
            {
              q: 'What kinds of repairs does it cover?',
              a: 'Electrical, plumbing, HVAC, carpentry, appliances, roofing, painting, small engines, drywall, flooring, garage doors, sprinklers — basically anything a licensed handyman or tradesman would touch. If it is dangerous, the buddy will tell you and offer to connect you to a local pro.',
            },
            {
              q: 'Can I use it on my phone?',
              a: 'Yes. DIY X-treme is built mobile-first. Voice input, camera, and nameplate scanning all work great on iPhone and Android. Bookmark https://diybot.org to your home screen for a fast one-tap launch.',
            },
            {
              q: 'How accurate is the AI?',
              a: 'Our repair engine runs on Google Gemini 3 Flash with a heavy safety-first system prompt. If the AI is not confident, it will ask you a clarifying question first — or recommend a professional. We never let the AI guess on things that could hurt you.',
            },
            {
              q: 'What if I need a real pro?',
              a: 'Every guide has a "Call a Pro" button. Tap it and we route you to a vetted local sponsor tradesman (Contact Electric in Lake Worth, FL is our first). No shame in it — safety first.',
            },
          ].map((item) => (
            <details key={item.q} className="industrial-card group" open={false}>
              <summary className="cursor-pointer flex items-center justify-between gap-4 font-display text-xl text-zinc-100 hover:text-amber-400 transition-colors">
                {item.q}
                <ArrowRight size={18} className="shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <p className="text-zinc-400 leading-relaxed mt-4">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative max-w-4xl mx-auto px-6 pb-24 text-center">
        <h2 className="font-display text-4xl sm:text-6xl text-zinc-100 tracking-tight leading-none mb-6">
          Your next repair is <span className="text-gradient-hot">90 seconds away.</span>
        </h2>
        <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
          Free to start. No credit card. Just describe your problem and we'll walk you through it.
        </p>
        <Link
          to="/auth?mode=signup"
          className="btn-primary text-lg inline-flex"
          data-testid="landing-cta-footer"
        >
          <Sparkles size={20} /> Start My First Fix <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
